import { useQuery, useMutation } from '@tanstack/react-query'
import { apiClient, authHeader } from '../lib/api'
import { UserRole } from '@tnf-recruit/shared'
import { useStudentProfile } from './useStudentProfile'
import { useRecruiterProfile } from './useRecruiterProfile'
import { useSubscription } from './useSubscription'
import { useAuth } from './useAuth'
import { useUser } from './useUser'

const STUDENT_FREE_TIER_LIMIT = 1000

/** Aligned with student dashboard / athlete profile gating. */
export const STUDENT_PROFILE_COMPLETE_MIN_PCT = 80

export function useOnboarding() {
  const { getAccessToken } = useAuth()
  const { data: dbUser } = useUser()
  const userRole = dbUser?.role as string | undefined
  const isStudent = userRole === UserRole.STUDENT
  const isRecruiter = userRole === UserRole.RECRUITER
  const isAdmin = userRole === UserRole.ADMIN

  const studentCount = useQuery({
    queryKey: ['studentCount'],
    queryFn: async () => {
      const { data } = await apiClient.get('/payments/student-count')
      return (data.data?.count ?? 0) as number
    },
  })

  const { profile: studentProfile } = useStudentProfile({ enabled: isStudent })
  const { profile: recruiterProfile } = useRecruiterProfile({ enabled: isRecruiter })
  const { subscription } = useSubscription({ enabled: isRecruiter })

  const createStudentCheckout = useMutation({
    mutationFn: async (body: { successUrl: string; cancelUrl: string }) => {
      const token = await getAccessToken()
      const { data } = await apiClient.post(
        '/payments/create-student-checkout',
        body,
        authHeader(token)
      )
      return data.data as { sessionId: string; url: string }
    },
  })

  const hasPaid = isStudent
    ? (studentProfile.data?.hasAccess ?? false)
    : isRecruiter
      ? subscription.data?.status === 'active'
      : false

  const needsPayment = isStudent
    ? (studentCount.data ?? 0) >= STUDENT_FREE_TIER_LIMIT && !hasPaid
    : isRecruiter
      ? !hasPaid
      : false

  const profileComplete = isStudent
    ? (studentProfile.data?.profileCompletion ?? 0) >= STUDENT_PROFILE_COMPLETE_MIN_PCT
    : isRecruiter
      ? Boolean(
          recruiterProfile.data?.position &&
          recruiterProfile.data?.contactInfo?.firstName &&
          recruiterProfile.data?.contactInfo?.lastName &&
          recruiterProfile.data?.contactInfo?.email
        )
      : false

  // Admin has no student/recruiter profile and no payment concept at all —
  // none of the onboarding/payment machinery above applies, so short-circuit
  // rather than let it fall through to permanently-false/permanently-loading.
  const onboardingComplete = isAdmin ? true : hasPaid && profileComplete
  const profileLoading = isStudent ? studentProfile.isLoading : recruiterProfile.isLoading
  const subscriptionLoading = isRecruiter ? subscription.isLoading : false
  const onboardingStatusLoading = isAdmin
    ? false
    : profileLoading || subscriptionLoading || (isStudent && studentCount.isLoading)

  return {
    userRole,
    isStudent,
    isRecruiter,
    isAdmin,
    hasPaid,
    needsPayment,
    profileComplete,
    onboardingComplete,
    studentCount: studentCount.data ?? 0,
    studentCountLoading: studentCount.isLoading,
    profileLoading,
    subscriptionLoading,
    onboardingStatusLoading,
    createStudentCheckout,
    subscription,
  }
}
